// GENERATED FILE — DO NOT EDIT.
// Source: hsos-shared/src/analytics/ — edit there, then run:
//   node scripts/sync-shared-modules.mjs
// Target: hsos-web vendored @hsos/shared

/**
 * Canonical Helm analytics event registry — the single source of truth.
 *
 * Every PostHog event fired by any Helm surface is declared here. `hsos-web`
 * and `hsos-alfred` import this module directly; `hos-orchestrator` and the
 * Supabase edge functions cannot (separate deploy roots, no `file:` dependency
 * across them), so the root `scripts/sync-shared-modules.mjs` generates a
 * copy into each and CI fails on drift. Add an event here first, always.
 *
 * Naming rules — enforced by `assertTaxonomyIsValid()` and the CI gate:
 *   • `snake_case`, no leading `$` (that namespace belongs to PostHog).
 *   • `<subject>_<past-tense verb>`; the subject leads so events group in the UI.
 *   • Never rename or delete a shipped event — history does not follow renames.
 *     Mark it `deprecated` and add the replacement instead.
 */

export type AnalyticsSurface =
  | "marketing"
  | "console"
  | "founder"
  | "orchestrator"
  | "edge"
  | "app";

export type AnalyticsDomain =
  | "acquisition"
  | "demo"
  | "auth"
  | "onboarding"
  | "billing"
  | "product"
  | "ai"
  | "knowledge"
  | "comms"
  | "workflow"
  | "outreach"
  | "conversion"
  | "ops"
  | "support";

export type EventDefinition = {
  /** Domain the event belongs to — drives dashboard grouping. */
  readonly domain: AnalyticsDomain;
  /** Surfaces allowed to emit it. A capture from anywhere else is a bug. */
  readonly surfaces: readonly AnalyticsSurface[];
  readonly description: string;
  /** Expected properties beyond the platform defaults (see `properties.ts`). */
  readonly properties?: readonly string[];
  /**
   * True when the event belongs to a tenant. Tenant-scoped events MUST carry
   * `organization_id` and be attached to the `organization` group, otherwise
   * every per-tenant insight silently under-counts.
   */
  readonly tenantScoped?: boolean;
  /** Still accepted for historical continuity; never emit from new code. */
  readonly deprecated?: string;
};

/* ────────────────────────────────────────────────────────────────────────────
   Registry
   ──────────────────────────────────────────────────────────────────────────── */

export const ANALYTICS_EVENTS = {
  /* ── Acquisition (marketing site, anonymous or lead-identified) ─────────── */

  cta_click: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "A primary or secondary call-to-action was clicked.",
    properties: ["label", "location", "href"],
  },
  demo_form_start: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor began submitting the demo-request form.",
  },
  demo_form_complete: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Demo-request form accepted client-side.",
  },
  demo_requested: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description:
      "Demo request processed server-side and the lead email was sent. Server-side truth for the demo funnel.",
    properties: ["name", "company"],
  },
  demo_request_received: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Legacy name for the server-side demo request.",
    deprecated: "Use demo_requested.",
  },
  /**
   * The marketing site's missed-call demo, which the visitor runs themselves.
   *
   * Deliberately NOT `missed_call_recovered`. That one is `domain: "comms"`,
   * `surfaces: ["orchestrator"]` and tenant-scoped — it fires when a real
   * recovery happens for a real shop. Reusing it for a button on a marketing
   * page would put simulated events into the product funnel that measures
   * whether the product works, which is the one number that must stay clean.
   */
  recovery_demo_run: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor ran the missed-call recovery demo on the marketing site.",
    properties: ["scenario"],
  },
  /**
   * The long-arc page, and the quiet band that points at it.
   *
   * Worth measuring separately from every other link because the band sits
   * BELOW the pilot ask by design — the buyer meets the request before the
   * ambition. If a meaningful share of visitors reach the ask and then leave
   * through this link instead, the placement rule is wrong and the numbers
   * are how anyone would find out.
   */
  vision_link_clicked: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor followed the link from the where-this-goes band to /vision.",
    properties: ["source"],
  },
  /*
   * There is deliberately no `vision_page_viewed`.
   *
   * The build pack asked for one and it would have been a second name for
   * `$pageview` filtered to a path, which PostHog captures already. A registry
   * that carries an event per page stops being a taxonomy and becomes a list of
   * routes, and the duplicate is the one people then disagree about — two
   * numbers for the same thing, neither obviously wrong.
   *
   * `vision_link_clicked` stays because it measures something `$pageview`
   * cannot: which surface sent the visitor, which is the only way to know
   * whether the band below the ask is pulling people away from it.
   */
  contact_form_submit: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Contact form accepted client-side.",
  },
  contact_message_received: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Contact message processed server-side and emailed.",
    properties: ["name", "company"],
  },
  email_capture_submit: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Email captured client-side from an inline capture block.",
    properties: ["source"],
  },
  email_capture_received: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Email capture confirmed server-side.",
    properties: ["source"],
  },
  roi_calculator_use: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor ran the ROI calculator.",
  },
  faq_expand: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "An FAQ accordion item was expanded.",
    properties: ["question"],
  },
  fit_quiz_complete: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor finished the qualification quiz.",
    properties: ["result"],
  },
  plan_picker_complete: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Visitor selected a plan in the pricing picker.",
    properties: ["plan"],
  },
  scale_quote_adjusted: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Scale-tier quote inputs were changed.",
  },
  scale_quote_checkout_click: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Checkout clicked from the scale-tier quote builder.",
  },
  enterprise_quote_viewed: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "A tokenized enterprise quote was opened.",
  },
  enterprise_quote_submitted: {
    domain: "acquisition",
    surfaces: ["marketing"],
    description: "Enterprise quote request submitted.",
  },

  /* ── Demo & tour ────────────────────────────────────────────────────────── */

  demo_mode_selected: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "Visitor chose a demo mode.",
    properties: ["mode"],
  },
  demo_path_selected: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "Visitor chose a demo path.",
    properties: ["path"],
  },
  demo_sandbox_message: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "A message was sent in the sandbox demo.",
    properties: ["scenario"],
  },
  /**
   * The prospect-attributed sandbox session — deliberately NOT
   * `demo_sandbox_message` above.
   *
   * That one is the public demo: keyed to an anonymous per-device id, with no
   * prospect journey behind it, and it counts a message rather than a session.
   * Cohort 1's funnel asks whether the shop we emailed on Tuesday opened the
   * sandbox and how far they got — a different person and a different
   * denominator, so reusing the public name would fuse two numbers nobody could
   * separate again.
   *
   * Both are captured SERVER-SIDE from the demo beacon
   * (`/api/demo/session-event`), alongside the `demo.started` /
   * `demo.question_asked` rows in `growth.journey_event` they mirror. Not from
   * the browser: the prospect session lives in an httpOnly cookie and the
   * journey id is never sent to the client at all, so a client capture could
   * not carry `prospect_journey_id` — the one property the whole funnel joins
   * on.
   */
  sandbox_started: {
    domain: "demo",
    surfaces: ["marketing"],
    description:
      "A prospect opened the sandbox. Mirrors the growth.event_kind demo.started.",
    properties: [
      "prospect_journey_id",
      "source",
      "outreach_code",
      "session_id",
    ],
  },
  sandbox_messages: {
    domain: "demo",
    surfaces: ["marketing"],
    description:
      "One message a prospect sent in the sandbox. Mirrors demo.question_asked; named plural because the dashboard aggregates the count, and fired once per message.",
    properties: ["prospect_journey_id", "message_index", "session_id"],
  },
  demo_public_qa: {
    domain: "demo",
    surfaces: ["marketing"],
    description:
      "Public demo Q&A asked from the browser. Pairs with the server-side demo_qa_asked via the same distinct ID.",
    properties: ["signed_in", "context"],
  },
  demo_qa_asked: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "Public demo Q&A answered server-side by the orchestrator.",
    properties: ["question", "answered", "confidence"],
  },
  demo_tour_started: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "Guided product tour started.",
  },
  demo_tour_step_viewed: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "A guided-tour step became visible.",
    properties: ["stepId", "index"],
  },
  demo_tour_question_asked: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "A question was asked inside the guided tour.",
    properties: ["question"],
  },
  demo_tour_show_me_where: {
    domain: "demo",
    surfaces: ["marketing"],
    description: '"Show me where" was used inside the guided tour.',
    properties: ["stepId"],
  },
  demo_tour_completed: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "Guided product tour completed.",
  },
  demo_tour_cta_clicked: {
    domain: "demo",
    surfaces: ["marketing"],
    description: "A CTA at the end of the guided tour was clicked.",
    properties: ["cta"],
  },

  /* ── Auth & account lifecycle ───────────────────────────────────────────── */

  signup_started: {
    domain: "auth",
    surfaces: ["marketing"],
    description: "Signup form submitted; account not yet created.",
  },
  signup_handoff: {
    domain: "auth",
    surfaces: ["marketing"],
    description: "Account created and the user was identified.",
    properties: ["email_confirmed"],
  },
  /*
   * The demand signal for a trade Helm has not launched. Fires INSTEAD of
   * checkout, so it is the only record that a shop in that trade wanted to buy
   * — count these per trade to decide what to launch next.
   */
  signup_trade_waitlisted: {
    domain: "auth",
    surfaces: ["marketing"],
    description:
      "A shop chose a trade that is not generally available, so signup ended in the waitlist instead of checkout.",
    properties: ["trade", "plan"],
  },
  login_handoff: {
    domain: "auth",
    surfaces: ["marketing"],
    description: "Sign-in succeeded and the user was identified.",
  },
  logout: {
    domain: "auth",
    surfaces: ["marketing", "console", "founder"],
    description: "User signed out; the analytics identity is reset.",
  },
  email_verified: {
    domain: "auth",
    surfaces: ["edge"],
    description: "Email address confirmed.",
  },
  password_reset_requested: {
    domain: "auth",
    surfaces: ["marketing", "edge"],
    description: "A password reset was requested.",
  },
  password_reset_completed: {
    domain: "auth",
    surfaces: ["marketing", "edge"],
    description: "A password reset was completed.",
  },
  mfa_enrolled: {
    domain: "auth",
    surfaces: ["console", "edge"],
    description: "A second factor was enrolled.",
    properties: ["factor_type"],
  },
  mfa_challenge_failed: {
    domain: "auth",
    surfaces: ["console", "edge"],
    description: "An MFA challenge failed — a security signal, not just UX.",
    properties: ["factor_type", "reason"],
  },
  passkey_registered: {
    domain: "auth",
    surfaces: ["console", "edge"],
    description: "A passkey credential was registered.",
  },
  org_switched: {
    domain: "auth",
    surfaces: ["console", "founder", "edge"],
    description: "Active organization changed; the JWT org claim was reissued.",
    tenantScoped: true,
    properties: ["from_organization_id"],
  },
  member_invited: {
    domain: "auth",
    surfaces: ["console", "edge"],
    description: "A teammate was invited to the organization.",
    tenantScoped: true,
    properties: ["role"],
  },
  member_joined: {
    domain: "auth",
    surfaces: ["console", "edge"],
    description: "An invited teammate accepted and joined.",
    tenantScoped: true,
    properties: ["role"],
  },

  /* ── Onboarding & activation ────────────────────────────────────────────── */

  setup_wizard_step: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "A setup wizard step was viewed.",
    tenantScoped: true,
    properties: ["step", "step_name"],
  },
  setup_wizard_step_completed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "A setup wizard step was completed.",
    tenantScoped: true,
    properties: ["step", "step_name"],
  },
  setup_wizard_core_completed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Core setup track finished — the console unlocked.",
    tenantScoped: true,
  },
  setup_wizard_completed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Full setup wizard completed.",
    tenantScoped: true,
  },
  kb_intake_submitted: {
    domain: "onboarding",
    surfaces: ["marketing", "console"],
    description: "Knowledge-base intake form submitted.",
  },
  kb_intake_received: {
    domain: "onboarding",
    surfaces: ["marketing"],
    description: "Knowledge-base intake confirmed server-side.",
    properties: ["company"],
  },
  kb_readiness_viewed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Knowledge readiness scorecard viewed.",
    tenantScoped: true,
    properties: ["score"],
  },
  onboarding_sandbox_test_completed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Sandbox test conversation completed.",
    tenantScoped: true,
  },
  onboarding_hitl_verified: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Human-in-the-loop review verified.",
    tenantScoped: true,
  },
  onboarding_readiness_reviewed: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Production readiness reviewed by the owner.",
    tenantScoped: true,
  },
  onboarding_carrier_waiting_acknowledged: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Owner acknowledged the A2P carrier wait.",
    tenantScoped: true,
  },
  onboarding_production_activated: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Production activated manually.",
    tenantScoped: true,
  },
  onboarding_production_auto_activated: {
    domain: "onboarding",
    surfaces: ["console", "edge"],
    description: "Production activated automatically once gates cleared.",
    tenantScoped: true,
  },
  onboarding_optimization_loop_configured: {
    domain: "onboarding",
    surfaces: ["console"],
    description: "Continuous optimization loop configured.",
    tenantScoped: true,
  },
  a2p_submitted: {
    domain: "onboarding",
    surfaces: ["console", "edge"],
    description: "A2P 10DLC registration submitted to the carrier.",
    tenantScoped: true,
  },
  a2p_approved: {
    domain: "onboarding",
    surfaces: ["edge"],
    description: "A2P 10DLC campaign approved — production SMS unblocked.",
    tenantScoped: true,
  },
  a2p_rejected: {
    domain: "onboarding",
    surfaces: ["edge"],
    description: "A2P 10DLC campaign rejected.",
    tenantScoped: true,
    properties: ["reason"],
  },
  autonomy_graduated: {
    domain: "onboarding",
    surfaces: ["console", "orchestrator"],
    description: "The org's AI autonomy level increased.",
    tenantScoped: true,
    properties: ["from_level", "to_level"],
  },

  /* ── Billing & revenue ──────────────────────────────────────────────────── */

  pilot_org_provisioned: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "A pilot organization was provisioned.",
    tenantScoped: true,
    properties: ["slug"],
  },
  pilot_checkout_started: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "Pilot checkout session started.",
    tenantScoped: true,
  },
  pilot_checkout_skipped: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "Pilot checkout was skipped.",
    tenantScoped: true,
    properties: ["reason"],
  },
  self_serve_org_provisioned: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "A self-serve organization was provisioned.",
    tenantScoped: true,
    properties: ["slug", "plan"],
  },
  self_serve_checkout_started: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "Self-serve checkout session started.",
    tenantScoped: true,
    properties: ["plan"],
  },
  self_serve_checkout_success: {
    domain: "billing",
    surfaces: ["marketing"],
    description: "Self-serve checkout completed in the browser.",
    tenantScoped: true,
    properties: ["plan"],
  },
  subscription_created: {
    domain: "billing",
    surfaces: ["edge"],
    description: "Stripe subscription created — revenue truth, not the client redirect.",
    tenantScoped: true,
    properties: ["plan", "interval", "amount_usd", "trial"],
  },
  subscription_updated: {
    domain: "billing",
    surfaces: ["edge"],
    description: "Stripe subscription changed plan, quantity or status.",
    tenantScoped: true,
    properties: ["plan", "previous_plan", "status", "amount_usd"],
  },
  subscription_canceled: {
    domain: "billing",
    surfaces: ["edge"],
    description: "Stripe subscription canceled — the churn event.",
    tenantScoped: true,
    properties: ["plan", "reason", "lifetime_days"],
  },
  trial_started: {
    domain: "billing",
    surfaces: ["edge"],
    description: "A trial period began.",
    tenantScoped: true,
    properties: ["plan", "trial_days"],
  },
  trial_ended: {
    domain: "billing",
    surfaces: ["edge"],
    description: "A trial period ended.",
    tenantScoped: true,
    properties: ["plan", "converted"],
  },
  invoice_paid: {
    domain: "billing",
    surfaces: ["edge"],
    description: "An invoice was paid.",
    tenantScoped: true,
    properties: ["amount_usd", "invoice_id"],
  },
  invoice_payment_failed: {
    domain: "billing",
    surfaces: ["edge"],
    description: "An invoice payment failed — the dunning trigger.",
    tenantScoped: true,
    properties: ["amount_usd", "invoice_id", "attempt"],
  },
  usage_limit_reached: {
    domain: "billing",
    surfaces: ["orchestrator", "edge"],
    description: "An org hit a metered usage limit.",
    tenantScoped: true,
    properties: ["limit_kind", "limit_value"],
  },

  /* ── Product usage (customer console) ───────────────────────────────────── */

  console_viewed: {
    domain: "product",
    surfaces: ["console"],
    description: "A console surface was opened. Drives adoption by feature area.",
    tenantScoped: true,
    properties: ["surface"],
  },
  conversation_opened: {
    domain: "product",
    surfaces: ["console"],
    description: "A conversation thread was opened.",
    tenantScoped: true,
    properties: ["channel"],
  },
  conversation_taken_over: {
    domain: "product",
    surfaces: ["console"],
    description: "A human took over an AI-handled conversation.",
    tenantScoped: true,
    properties: ["channel", "reason"],
  },
  conversation_released: {
    domain: "product",
    surfaces: ["console"],
    description: "A human returned a conversation to the AI.",
    tenantScoped: true,
    properties: ["channel"],
  },
  job_created: {
    domain: "product",
    surfaces: ["console", "app", "orchestrator"],
    description: "A job was created.",
    tenantScoped: true,
    properties: ["source"],
  },
  customer_created: {
    domain: "product",
    surfaces: ["console", "orchestrator"],
    description: "A customer record was created.",
    tenantScoped: true,
    properties: ["source"],
  },
  settings_updated: {
    domain: "product",
    surfaces: ["console"],
    description: "Org settings changed.",
    tenantScoped: true,
    properties: ["section"],
  },
  automation_enabled: {
    domain: "product",
    surfaces: ["console"],
    description: "An automation was switched on.",
    tenantScoped: true,
    properties: ["automation_key"],
  },
  automation_disabled: {
    domain: "product",
    surfaces: ["console"],
    description: "An automation was switched off.",
    tenantScoped: true,
    properties: ["automation_key"],
  },
  report_viewed: {
    domain: "product",
    surfaces: ["console"],
    description: "An analytics or report surface was viewed.",
    tenantScoped: true,
    properties: ["report"],
  },

  /* ── AI plane ───────────────────────────────────────────────────────────── */

  ai_turn_completed: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "An AI turn finished. The core AI-usage metric.",
    tenantScoped: true,
    properties: [
      "conversation_id",
      "channel",
      "outcome",
      "agent_key",
      "run_id",
      "copilot_mode",
      "model",
      "provider",
      "latency_ms",
      "input_tokens",
      "output_tokens",
      "cost_usd",
    ],
  },
  ai_run_failed: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "An AI run failed after exhausting provider fallbacks.",
    tenantScoped: true,
    properties: ["agent_key", "error_code", "model", "provider"],
  },
  ai_model_fallback: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description:
      "The model router fell through to a fallback candidate — reliability signal for the routing table.",
    tenantScoped: true,
    properties: ["from_model", "to_model", "reason", "task"],
  },
  ai_tool_invoked: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "A typed tool was invoked by the AI.",
    tenantScoped: true,
    properties: ["tool", "outcome", "latency_ms"],
  },
  ai_guardrail_tripped: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "A Layer-0 or tenant guardrail blocked an AI action.",
    tenantScoped: true,
    properties: ["guardrail", "severity", "agent_key"],
  },
  ai_escalation_raised: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "The AI escalated a conversation to a human.",
    tenantScoped: true,
    properties: ["reason", "channel", "confidence"],
  },
  ai_confidence_low: {
    domain: "ai",
    surfaces: ["orchestrator"],
    description: "A turn completed below the confidence threshold.",
    tenantScoped: true,
    properties: ["confidence", "threshold", "agent_key"],
  },
  copilot_suggestion_shown: {
    domain: "ai",
    surfaces: ["console", "orchestrator"],
    description: "A copilot draft was surfaced to an operator.",
    tenantScoped: true,
    properties: ["surface"],
  },
  copilot_suggestion_accepted: {
    domain: "ai",
    surfaces: ["console"],
    description: "An operator accepted a copilot draft — the AI quality signal.",
    tenantScoped: true,
    properties: ["surface", "edited"],
  },

  /* ── Knowledge plane ────────────────────────────────────────────────────── */

  kb_document_ingested: {
    domain: "knowledge",
    surfaces: ["orchestrator", "edge"],
    description: "A knowledge document was ingested and chunked.",
    tenantScoped: true,
    properties: ["source", "chunks"],
  },
  kb_search_performed: {
    domain: "knowledge",
    surfaces: ["orchestrator"],
    description: "Knowledge retrieval ran for a turn.",
    tenantScoped: true,
    properties: ["strategy", "results", "top_score", "latency_ms"],
  },
  kb_answer_grounded: {
    domain: "knowledge",
    surfaces: ["orchestrator"],
    description: "An answer was grounded in retrieved knowledge.",
    tenantScoped: true,
    properties: ["chunks_used", "top_score"],
  },
  kb_answer_ungrounded: {
    domain: "knowledge",
    surfaces: ["orchestrator"],
    description:
      "An answer had no supporting knowledge — the coverage-gap signal.",
    tenantScoped: true,
    properties: ["query"],
  },
  kb_gap_detected: {
    domain: "knowledge",
    surfaces: ["orchestrator"],
    description: "A recurring knowledge gap was detected for the org.",
    tenantScoped: true,
    properties: ["topic", "occurrences"],
  },

  /* ── Communications ─────────────────────────────────────────────────────── */

  sms_inbound_received: {
    domain: "comms",
    surfaces: ["edge"],
    description: "An inbound SMS arrived — the top of the product funnel.",
    tenantScoped: true,
    properties: ["conversation_id", "from_hash"],
  },
  sms_outbound_sent: {
    domain: "comms",
    surfaces: ["edge", "orchestrator"],
    description: "An outbound SMS was handed to the carrier.",
    tenantScoped: true,
    properties: ["conversation_id", "sender"],
  },
  sms_delivery_failed: {
    domain: "comms",
    surfaces: ["edge"],
    description: "Carrier reported an SMS delivery failure.",
    tenantScoped: true,
    properties: ["error_code", "conversation_id"],
  },
  voice_call_started: {
    domain: "comms",
    surfaces: ["edge", "orchestrator"],
    description: "A voice call began.",
    tenantScoped: true,
    properties: ["direction"],
  },
  voice_call_completed: {
    domain: "comms",
    surfaces: ["edge", "orchestrator"],
    description: "A voice call ended.",
    tenantScoped: true,
    properties: ["direction", "duration_seconds", "outcome"],
  },
  missed_call_recovered: {
    domain: "comms",
    surfaces: ["orchestrator"],
    description: "A missed call was followed up automatically.",
    tenantScoped: true,
  },
  email_sent: {
    domain: "comms",
    surfaces: ["edge"],
    description: "A transactional email was sent.",
    tenantScoped: true,
    properties: ["template"],
  },
  email_bounced: {
    domain: "comms",
    surfaces: ["edge"],
    description: "A transactional email bounced.",
    tenantScoped: true,
    properties: ["template", "reason"],
  },

  /* ── Workflow & automation ──────────────────────────────────────────────── */

  workflow_triggered: {
    domain: "workflow",
    surfaces: ["edge", "orchestrator"],
    description: "An n8n workflow or scheduled tick started.",
    tenantScoped: true,
    properties: ["workflow", "trigger"],
  },
  workflow_completed: {
    domain: "workflow",
    surfaces: ["edge", "orchestrator"],
    description: "A workflow run finished successfully.",
    tenantScoped: true,
    properties: ["workflow", "duration_ms"],
  },
  workflow_failed: {
    domain: "workflow",
    surfaces: ["edge", "orchestrator"],
    description: "A workflow run failed.",
    tenantScoped: true,
    properties: ["workflow", "error_code"],
  },
  automation_suggested: {
    domain: "workflow",
    surfaces: ["orchestrator"],
    description: 'A "Suggested by Helm" automation was proposed to an org.',
    tenantScoped: true,
    properties: ["automation_key", "confidence"],
  },
  automation_accepted: {
    domain: "workflow",
    surfaces: ["console"],
    description: "An owner accepted a suggested automation.",
    tenantScoped: true,
    properties: ["automation_key"],
  },

  /* ── Outbound growth (platform org) ─────────────────────────────────────── */

  sms_outreach_lead_qualified: {
    domain: "outreach",
    surfaces: ["orchestrator"],
    description: "An outreach lead was qualified.",
    tenantScoped: true,
    properties: ["lead_id", "approved", "confidence", "classification", "used_llm"],
  },
  sms_outreach_scheduled: {
    domain: "outreach",
    surfaces: ["orchestrator"],
    description: "An outreach message was scheduled into a send window.",
    tenantScoped: true,
    properties: ["lead_id", "step", "window_slot", "scheduled_for"],
  },
  sms_outreach_reply: {
    domain: "outreach",
    surfaces: ["orchestrator"],
    description: "A reply to outreach was classified.",
    tenantScoped: true,
    properties: ["lead_id", "label", "confidence"],
  },
  sms_outreach_delivery_triggered: {
    domain: "outreach",
    surfaces: ["orchestrator"],
    description: "An outreach delivery message was triggered.",
    tenantScoped: true,
    properties: ["lead_id"],
  },
  sms_outreach_sent: {
    domain: "outreach",
    surfaces: ["orchestrator"],
    description: "A scheduled outreach message was handed to the carrier.",
    tenantScoped: true,
    properties: ["lead_id", "step"],
  },
  sms_outreach_inbound: {
    domain: "outreach",
    surfaces: ["edge"],
    description: "An inbound outreach reply arrived.",
    tenantScoped: true,
  },
  sms_outreach_opt_out: {
    domain: "outreach",
    surfaces: ["edge"],
    description: "A recipient opted out of outreach.",
    tenantScoped: true,
    properties: ["phone_hash", "source"],
  },
  /**
   * The Revenue Leak Audit page, one per prospect, opened from a cold email.
   *
   * ── CAPTURED SERVER-SIDE, AND NOT FOR EVERY REQUEST ───────────────────────
   *
   * The page is a server component and the reader may block analytics, so a
   * client capture would under-count precisely the audience cohort 1 is made
   * of. And corporate mail security — Safe Links, Proofpoint, Mimecast,
   * Barracuda — fetches every URL in a message before the recipient sees it, so
   * a capture per request would put the view rate at ~100% and mean nothing.
   * The capture site requires an `Accept` that asks for `text/html`; this is the
   * same hazard that stopped the unsubscribe GET writing.
   *
   * `rows_shown` is the property worth having: it measures how thin the pages
   * are. A cohort averaging two rows is a cohort whose enrichment is not
   * finished, and that shows up here a week before it shows up in the reply
   * rate.
   */
  audit_viewed: {
    domain: "outreach",
    surfaces: ["marketing"],
    description:
      "A prospect opened their own audit page. Suppressed for requests that do not look like a browser navigation.",
    properties: [
      "prospect_journey_id",
      "outreach_code",
      "touch_day",
      "has_hours",
      "has_booking_signal",
      "estimate_monthly_usd",
      "rows_shown",
    ],
  },
  /**
   * A prospect booked a closing call with us — the outreach programme's outcome.
   *
   * Deliberately NOT `booking_created` below. That one is tenant-scoped and
   * fires when the AI books a job for a shop; this is a shop owner booking time
   * with Helm, on the platform org. Putting a sales meeting into the funnel that
   * measures whether the product works would corrupt the one number that must
   * stay clean.
   */
  call_booked: {
    domain: "outreach",
    surfaces: ["marketing"],
    description:
      "A prospect booked a closing call. Mirrors the growth.event_kind sales.call_booked, which the booking RPC emits in the same transaction.",
    properties: [
      "prospect_journey_id",
      "source",
      "days_since_first_touch",
      "touch_day",
    ],
  },

  /* ── Conversion (the money moment in the product) ───────────────────────── */

  booking_created: {
    domain: "conversion",
    surfaces: ["orchestrator"],
    description:
      "The AI booked a job. The outcome every other product metric exists to move.",
    tenantScoped: true,
    properties: [
      "conversation_id",
      "job_id",
      "appointment_id",
      "service_id",
      "correlation_id",
    ],
  },
  booking_canceled: {
    domain: "conversion",
    surfaces: ["console", "orchestrator"],
    description: "A booked job was canceled.",
    tenantScoped: true,
    properties: ["job_id", "reason"],
  },
  quote_sent: {
    domain: "conversion",
    surfaces: ["console", "orchestrator"],
    description: "A quote was sent to a customer.",
    tenantScoped: true,
    properties: ["amount_usd"],
  },
  quote_accepted: {
    domain: "conversion",
    surfaces: ["console", "orchestrator"],
    description: "A customer accepted a quote.",
    tenantScoped: true,
    properties: ["amount_usd"],
  },

  /* ── Founder / Alfred operations ────────────────────────────────────────── */

  founder_console_viewed: {
    domain: "ops",
    surfaces: ["founder"],
    description: "A founder HQ surface was opened.",
    properties: ["surface"],
  },
  alfred_action_proposed: {
    domain: "ops",
    surfaces: ["orchestrator"],
    description: "Alfred enqueued a Tier-2 action for approval.",
    properties: ["agent", "action_kind", "severity"],
  },
  alfred_action_approved: {
    domain: "ops",
    surfaces: ["founder", "edge"],
    description: "A founder approved an Alfred action.",
    properties: ["agent", "action_kind"],
  },
  alfred_action_rejected: {
    domain: "ops",
    surfaces: ["founder", "edge"],
    description: "A founder rejected an Alfred action.",
    properties: ["agent", "action_kind", "reason"],
  },
  alfred_action_executed: {
    domain: "ops",
    surfaces: ["edge", "orchestrator"],
    description: "An approved Alfred action executed.",
    properties: ["agent", "action_kind", "outcome"],
  },
  alfred_tick_completed: {
    domain: "ops",
    surfaces: ["orchestrator"],
    description: "The Alfred executive tick finished a cycle.",
    properties: ["agents_run", "actions_created", "duration_ms"],
  },

  /* ── Support ────────────────────────────────────────────────────────────── */

  support_ticket_created: {
    domain: "support",
    surfaces: ["console", "edge"],
    description: "A support ticket was raised.",
    tenantScoped: true,
    properties: ["category", "severity"],
  },
} as const satisfies Record<string, EventDefinition>;

/* ────────────────────────────────────────────────────────────────────────────
   Derived types & helpers
   ──────────────────────────────────────────────────────────────────────────── */

export type AnalyticsEventName = keyof typeof ANALYTICS_EVENTS;

/** Back-compat alias for the pre-registry union in `hsos-web/lib/analytics.ts`. */
export type AnalyticsEvent = AnalyticsEventName;

export const ANALYTICS_EVENT_NAMES = Object.keys(
  ANALYTICS_EVENTS,
) as AnalyticsEventName[];

/**
 * Widen a registry entry to `EventDefinition`.
 *
 * `satisfies` keeps each entry's literal type, which means optional fields are
 * absent (not `undefined`) on entries that omit them — so the union cannot be
 * read generically without this. Every consumer goes through here.
 */
export function eventDefinition(name: AnalyticsEventName): EventDefinition {
  return ANALYTICS_EVENTS[name];
}

export function isTenantScoped(name: AnalyticsEventName): boolean {
  return eventDefinition(name).tenantScoped === true;
}

export function eventsForSurface(
  surface: AnalyticsSurface,
): AnalyticsEventName[] {
  return ANALYTICS_EVENT_NAMES.filter((name) =>
    (ANALYTICS_EVENTS[name].surfaces as readonly string[]).includes(surface),
  );
}

export function eventsForDomain(domain: AnalyticsDomain): AnalyticsEventName[] {
  return ANALYTICS_EVENT_NAMES.filter(
    (name) => ANALYTICS_EVENTS[name].domain === domain,
  );
}

const EVENT_NAME_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

/**
 * Structural validation of the registry itself. Runs in unit tests and in the
 * taxonomy CI gate so a malformed entry never reaches a capture call.
 */
export function assertTaxonomyIsValid(): void {
  const problems: string[] = [];

  for (const name of ANALYTICS_EVENT_NAMES) {
    const definition = eventDefinition(name);

    if (!EVENT_NAME_PATTERN.test(name)) {
      problems.push(`${name}: not snake_case, or starts with a digit/$`);
    }
    if (definition.surfaces.length === 0) {
      problems.push(`${name}: declares no surface`);
    }
    if (definition.description.trim().length < 10) {
      problems.push(`${name}: description is missing or too short`);
    }
    if (definition.properties?.some((property) => property.startsWith("$"))) {
      problems.push(`${name}: property names must not start with $`);
    }
  }

  if (problems.length > 0) {
    throw new Error(
      `Invalid analytics taxonomy:\n  - ${problems.join("\n  - ")}`,
    );
  }
}
